import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { useState } from "react";

import { SvgIconField } from "../components/StorefrontFields";
import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { useStorefrontConfig } from "../lib/storefront";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Intercom live chat for one storefront environment. One platform workspace answers every
 * store; what a tenant decides here is whether their shoppers see the launcher (the switch on
 * the header, saved on flip) and what it looks like (the icon, saved from its form). The app id
 * is stage config, so on a stage without one the switch is disabled and the page says so.
 *
 * Two mutations over the same query: the icon's drops its draft on save, and the switch's must
 * not, or a flip would discard an icon a tenant was still editing.
 */
export function StorefrontSupportPage() {
  const [iconSvg, setIconSvg] = useState<string | null>(null);
  const { query, update: saveIcon } = useStorefrontConfig({ onSaved: () => setIconSvg(null) });
  const { update: saveSwitch } = useStorefrontConfig();
  const { can } = useTenantRole();

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment, intercomAvailable } = query.data;
  const currentIcon = iconSvg ?? config.intercomLauncherIconSvg;
  const canEdit = can("editor");
  const pending = saveIcon.isPending || saveSwitch.isPending;

  const submitIcon = (e: React.FormEvent) => {
    e.preventDefault();
    // Only once touched: an untouched icon shouldn't be re-validated, so a stored one can never
    // block the save.
    if (iconSvg === null) return;
    saveIcon.mutate({ intercomLauncherIconSvg: iconSvg });
  };

  return (
    <StorefrontSection
      title="Support chat"
      description="Intercom live chat for shoppers on this storefront. Conversations land in the Loot Labs support workspace, tagged with this store, so agents see which storefront a shopper wrote from; sandboxes use a separate test workspace and never reach live conversations."
      environment={environment}
      aside={
        <SectionSwitch
          id="intercom-enabled"
          label="Support chat"
          checked={config.intercomEnabled}
          disabled={!intercomAvailable || pending || !canEdit}
          onCheckedChange={(checked) => saveSwitch.mutate({ intercomEnabled: checked })}
        />
      }
    >
      <Card>
        <CardContent>
          <form className="grid gap-5" onSubmit={submitIcon}>
            {!intercomAvailable && (
              <p className="text-sm text-muted-foreground">
                Live chat is not available for{" "}
                {environment.type === "live" ? "live storefronts" : "sandbox storefronts"} on this
                stage: it has no Intercom workspace configured for them. Your settings are kept and
                take effect once it does.
              </p>
            )}
            <div className="max-w-xl">
              <SvgIconField
                id="intercom-launcher-icon"
                label="Launcher icon (SVG)"
                value={currentIcon}
                onChange={setIconSvg}
                fallback={
                  <span aria-hidden className="text-lg leading-none">
                    💬
                  </span>
                }
                help="Shown on the chat button in the storefront's corner, on your theme's primary color. Flat SVG shapes only — no scripts or linked files. Falls back to the default chat glyph when unset."
              />
            </div>

            <div>
              <Button type="submit" disabled={pending || !canEdit || iconSvg === null}>
                {saveIcon.isPending ? "Saving…" : "Save launcher icon"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </StorefrontSection>
  );
}
